// =====================================================================
// Cart store — local-first persistence backed by Telegram CloudStorage
// with localStorage fallback. Hydration is explicit via `hydrate()`
// (called once from the app shell) so the store stays synchronous for
// selectors and the UI never has to read `null`.
// =====================================================================

import { create } from "zustand";
import { CartItemSchema, type CartItem, type CartDraft } from "@/shared/lib/db-schema";
import { loadCart, saveCart } from "./storage";

interface CartState {
  items: ReadonlyArray<CartItem>;
  hydrated: boolean;
  hydrate: () => Promise<void>;
  add: (item: CartDraft, qty?: number) => Promise<void>;
  setQty: (productId: string, color: string, size: string, qty: number) => Promise<void>;
  remove: (productId: string, color: string, size: string) => Promise<void>;
  clear: () => Promise<void>;
}

export const useCartStore = create<CartState>()((set, get) => ({
  items: [],
  hydrated: false,

  hydrate: async () => {
    if (get().hydrated) return;
    const loaded = await loadCart();
    const parsed: CartItem[] = [];
    for (const row of loaded) {
      const r = CartItemSchema.safeParse(row);
      if (r.success) parsed.push(r.data);
    }
    set({ items: parsed, hydrated: true });
  },

  add: async (item, qty = 1) => {
    const items = get().items;
    const idx = items.findIndex(
      (c) => c.productId === item.productId && c.color === item.color && c.size === item.size,
    );
    const next: ReadonlyArray<CartItem> =
      idx >= 0
        ? items.map((c, i) => (i === idx ? { ...c, qty: c.qty + qty } : c))
        : [...items, { ...item, qty }];
    set({ items: next });
    await saveCart([...next]);
  },

  setQty: async (productId, color, size, qty) => {
    const items = get().items;
    const next: ReadonlyArray<CartItem> =
      qty <= 0
        ? items.filter((c) => !(c.productId === productId && c.color === color && c.size === size))
        : items.map((c) =>
            c.productId === productId && c.color === color && c.size === size ? { ...c, qty } : c,
          );
    set({ items: next });
    await saveCart([...next]);
  },

  remove: async (productId, color, size) => {
    const items = get().items;
    const next = items.filter((c) => !(c.productId === productId && c.color === color && c.size === size));
    set({ items: next });
    await saveCart([...next]);
  },

  clear: async () => {
    set({ items: [] });
    await saveCart([]);
  },
}));

// ---------------------------------------------------------------------
// Thin selector hooks (zustand-recommended pattern). No useState /
// useEffect — these are just `useStore` re-subscriptions.
// ---------------------------------------------------------------------
export const useCartItems = () => useCartStore((s) => s.items);
export const useCartHydrated = () => useCartStore((s) => s.hydrated);
export const useCartAdd = () => useCartStore((s) => s.add);
export const useCartSetQty = () => useCartStore((s) => s.setQty);
export const useCartRemove = () => useCartStore((s) => s.remove);
export const useCartClear = () => useCartStore((s) => s.clear);

export const useCartCount = (): number =>
  useCartStore((s) => s.items.reduce((sum, i) => sum + i.qty, 0));

export const useCartTotal = (): number =>
  useCartStore((s) => s.items.reduce((sum, i) => sum + i.qty * i.price, 0));
